import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Button,
  IconButton,
  Typography,
  Stack,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { SIGNATURE_COLORS } from './signatureConstants';

export default function SignatureCanvas({ height, onCapture }) {
  const [selectedColor, setSelectedColor] = useState(SIGNATURE_COLORS[0].value);
  const [lineWidth, setLineWidth] = useState(2);
  const [isDrawing, setIsDrawing] = useState(false);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const containerRef = useRef(null);

  // Setup canvas and context
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions to match display size
      resizeCanvas();
      
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = selectedColor;
      ctx.lineWidth = lineWidth;
      ctxRef.current = ctx;
      saveDrawState();
    }
    
    // Add resize event listener
    window.addEventListener('resize', resizeCanvas);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  // Function to resize canvas to match display size
  const resizeCanvas = () => {
    if (canvasRef.current && containerRef.current) {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      
      // Get the container's display width
      const containerWidth = container.clientWidth;
      
      // Set canvas dimensions to actual pixel values for proper rendering
      canvas.width = containerWidth;
      canvas.height = height || 200; // Use provided height or default to 200
      
      // Restore context properties after resize
      if (ctxRef.current) {
        ctxRef.current.lineCap = 'round';
        ctxRef.current.lineJoin = 'round';
        ctxRef.current.strokeStyle = selectedColor;
        ctxRef.current.lineWidth = lineWidth;
      }
    }
  };

  // Update context when color or line width changes
  useEffect(() => {
    if (ctxRef.current) {
      ctxRef.current.strokeStyle = selectedColor;
      ctxRef.current.lineWidth = lineWidth;
    }
  }, [selectedColor, lineWidth]);

  const saveDrawState = () => {
    if (canvasRef.current) {
      const imageData = canvasRef.current.toDataURL('image/png');
      setUndoStack((prev) => [...prev, imageData]);
      setRedoStack([]);
    }
  };

  const getCoords = (e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate scale factors
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Get coordinates
    const clientX = e.clientX || (e.touches && e.touches[0].clientX);
    const clientY = e.clientY || (e.touches && e.touches[0].clientY);
    
    // Apply scaling to get accurate canvas coordinates
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    return { x, y };
  };

  const handleMouseDown = (e) => {
    setIsDrawing(true);
    const { x, y } = getCoords(e);
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(x, y);
    saveDrawState();
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    const { x, y } = getCoords(e);
    ctxRef.current.lineTo(x, y);
    ctxRef.current.stroke();
  };

  const handleMouseUp = () => setIsDrawing(false);
  
  const handleTouchStart = (e) => {
    e.preventDefault();
    handleMouseDown(e);
  };
  
  const handleTouchMove = (e) => {
    e.preventDefault();
    handleMouseMove(e);
  };
  
  const handleTouchEnd = () => handleMouseUp();

  const handleUndo = () => {
    if (undoStack.length > 1) {
      const currentState = canvasRef.current.toDataURL('image/png');
      const newUndoStack = undoStack.slice(0, -1);
      const lastState = newUndoStack[newUndoStack.length - 1];
      setRedoStack((prev) => [...prev, currentState]);
      const img = new Image();
      img.onload = () => {
        ctxRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctxRef.current.drawImage(img, 0, 0);
      };
      img.src = lastState;
      setUndoStack(newUndoStack);
    }
  };

  const handleRedo = () => {
    if (redoStack.length > 0) {
      const nextState = redoStack[redoStack.length - 1];
      const newRedoStack = redoStack.slice(0, -1);
      const currentState = canvasRef.current.toDataURL('image/png');
      setUndoStack((prev) => [...prev, currentState]);
      const img = new Image();
      img.onload = () => {
        ctxRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctxRef.current.drawImage(img, 0, 0);
      };
      img.src = nextState;
      setRedoStack(newRedoStack);
    }
  };

  const handleClear = () => {
    saveDrawState();
    ctxRef.current.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const handleCapture = () => {
    if (canvasRef.current) {
      // Crop signature to content bounds before capturing
      const croppedData = cropSignatureToContent();
      onCapture(croppedData);
    }
  };
  
  // Function to crop the signature to its actual content
  const cropSignatureToContent = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    // Find the bounds of the signature
    let minX = canvas.width;
    let minY = canvas.height;
    let maxX = 0;
    let maxY = 0;
    let found = false;
    
    // Scan through all pixels to find the boundaries
    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const alpha = data[(y * canvas.width + x) * 4 + 3];
        if (alpha > 0) {
          found = true;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }
    
    // If no content is found, return the original
    if (!found) {
      return canvas.toDataURL('image/png');
    }
    
    // Add padding
    const padding = 10;
    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX = Math.min(canvas.width, maxX + padding);
    maxY = Math.min(canvas.height, maxY + padding);
    
    // Create a new canvas with the exact size of the signature
    const width = maxX - minX;
    const height = maxY - minY;
    
    // Ensure we have valid dimensions
    if (width <= 0 || height <= 0) {
      return canvas.toDataURL('image/png');
    }
    
    // Create a temporary canvas for cropping
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d');
    
    // Copy only the signature part to the temp canvas
    tempCtx.drawImage(
      canvas, 
      minX, minY, width, height,
      0, 0, width, height
    );
    
    return tempCanvas.toDataURL('image/png');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>Ink Color</InputLabel>
            <Select
              value={selectedColor}
              label="Ink Color"
              onChange={(e) => setSelectedColor(e.target.value)}
            >
              {SIGNATURE_COLORS.map((color) => (
                <MenuItem key={color.name} value={color.value}>
                  {color.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography gutterBottom>Line Width: {lineWidth}px</Typography>
          <Slider
            value={lineWidth}
            min={1}
            max={8}
            step={0.5}
            onChange={(e, val) => setLineWidth(val)}
            aria-labelledby="line-width-slider"
          />
        </Grid>

        <Grid item xs={12} sm={12} md={4} display="flex" alignItems="center" gap={1}>
          <IconButton onClick={handleUndo} disabled={undoStack.length <= 1}>
            <UndoIcon color={undoStack.length <= 1 ? 'disabled' : 'primary'} />
          </IconButton>
          <IconButton onClick={handleRedo} disabled={redoStack.length === 0}>
            <RedoIcon color={redoStack.length === 0 ? 'disabled' : 'primary'} />
          </IconButton>
        </Grid>
      </Grid>

      <Box 
        ref={containerRef} 
        sx={{ 
          width: '100%', 
          height: 'auto' 
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          sx={{
            border: '1px solid #ccc',
            borderRadius: 2,
            backgroundColor: '#fafafa',
            width: '100%',
            touchAction: 'none',
            display: 'block', // Important for proper sizing
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        />
      </Box>

      <Stack direction="row" justifyContent="flex-end" spacing={2}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={handleClear}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveAltIcon />}
          onClick={handleCapture}
        >
          Capture
        </Button>
      </Stack>
    </Box>
  );
}